import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useParticipante } from '../../../contexts/ParticipanteContext';

const ParticipanteMissoes: React.FC = () => {
  const { campanhaAtual } = useParticipante();
  const [filtro, setFiltro] = useState<'abertas' | 'andamento' | 'concluidas'>('abertas');

  // Usar missões da campanha atual do contexto
  const missoes = useMemo(() => {
    if (!campanhaAtual?.progressao?.missoesAtivas) return [];
    return campanhaAtual.progressao.missoesAtivas;
  }, [campanhaAtual]);

  const missoesFiltradas = missoes.filter(m => {
    if (filtro === 'abertas') return !m.concluida && m.progresso === 0;
    if (filtro === 'andamento') return !m.concluida && m.progresso > 0;
    if (filtro === 'concluidas') return m.concluida;
    return true;
  });

  const estatisticas = {
    abertas: missoes.filter(m => !m.concluida && m.progresso === 0).length,
    andamento: missoes.filter(m => !m.concluida && m.progresso > 0).length,
    concluidas: missoes.filter(m => m.concluida).length,
    xpTotal: missoes.filter(m => m.concluida).reduce((acc, m) => acc + m.xpRecompensa, 0)
  };

  // Se não houver campanha selecionada
  if (!campanhaAtual) {
    return (
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="bg-white rounded-2xl shadow-xl p-12">
            <i className="ri-flag-line text-6xl text-slate-300 mb-6"></i>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Nenhuma Campanha Selecionada
            </h2>
            <p className="text-slate-600 mb-8">
              Selecione uma campanha no header para visualizar as missões disponíveis.
            </p>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/premiado/campanhas')}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap"
            >
              Ver Campanhas Disponíveis
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getCorCategoria = (categoria: string) => {
    const cores: Record<string, string> = {
      'Rotina': 'blue',
      'Vendas': 'orange',
      'Suporte': 'purple',
      'Clientes': 'teal',
      'Jogos': 'pink',
      'Social': 'cyan',
      'Elite': 'yellow',
      'Distintivos': 'red',
      'Onboarding': 'green',
      'Marco': 'indigo',
      'Relacionamento': 'violet'
    };
    return cores[categoria] || 'slate';
  };

  const calcularDiasRestantes = (dataVencimento: string) => {
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    const diff = Math.ceil((vencimento.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diff < 0) return 'Expirada';
    if (diff === 0) return 'Hoje';
    if (diff === 1) return '1 dia';
    return `${diff} dias`;
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Missões</h1>
          <p className="text-sm text-slate-600 mt-1">
            Complete missões e ganhe XP na campanha <strong>{campanhaAtual.nome}</strong>
          </p>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
            <i className="ri-flag-line text-2xl text-blue-600"></i>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-1">{estatisticas.abertas}</p>
          <p className="text-sm text-slate-600">Abertas</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
            <i className="ri-time-line text-2xl text-orange-600"></i>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-1">{estatisticas.andamento}</p>
          <p className="text-sm text-slate-600">Em Andamento</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
            <i className="ri-check-line text-2xl text-green-600"></i>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-1">{estatisticas.concluidas}</p>
          <p className="text-sm text-slate-600">Concluídas</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-3">
            <i className="ri-star-line text-2xl text-yellow-600"></i>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-1">{estatisticas.xpTotal}</p>
          <p className="text-sm text-slate-600">XP Ganho</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center space-x-2 bg-white rounded-lg p-1 border border-slate-200 shadow-sm">
        {[
          { id: 'abertas', label: 'Abertas', icon: 'ri-flag-line' },
          { id: 'andamento', label: 'Em Andamento', icon: 'ri-time-line' },
          { id: 'concluidas', label: 'Concluídas', icon: 'ri-check-line' }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setFiltro(item.id as any)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              filtro === item.id
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <i className={`${item.icon} mr-2`}></i>
            {item.label}
          </button>
        ))}
      </div>

      {/* Lista de Missões */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {missoesFiltradas.map((missao) => {
          const cor = getCorCategoria(missao.categoria);
          const diasRestantes = calcularDiasRestantes(missao.dataVencimento);
          const progressoPercentual = (missao.progresso / missao.meta) * 100;

          return (
            <div
              key={missao.id}
              className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-lg transition-all hover:-translate-y-1"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold text-white bg-${cor}-500`}>
                      {missao.categoria}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                      {missao.tipo.charAt(0).toUpperCase() + missao.tipo.slice(1)}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900">{missao.nome}</h3>
                  <p className="text-sm text-slate-600 mt-1">{missao.descricao}</p>
                </div>
                <div className="ml-4 text-right">
                  <div className="flex items-center space-x-1 text-yellow-600">
                    <i className="ri-star-fill text-xl"></i>
                    <span className="text-lg font-bold">{missao.xpRecompensa}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">XP</p>
                </div>
              </div>

              {/* Progresso */}
              {!missao.concluida && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-700">Progresso</span>
                    <span className="text-sm font-bold text-slate-900">
                      {missao.progresso.toLocaleString('pt-BR')} / {missao.meta.toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r from-${cor}-500 to-${cor}-600 rounded-full transition-all`}
                      style={{ width: `${progressoPercentual}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Informações Adicionais */}
              <div className="space-y-2">
                <div className="flex items-center text-sm text-slate-600">
                  <i className="ri-gift-line mr-2"></i>
                  <span>{missao.pontosRecompensa} pontos de recompensa</span>
                </div>
                {!missao.concluida && diasRestantes !== 'Expirada' && (
                  <div className="flex items-center text-sm text-orange-600 font-medium">
                    <i className="ri-time-line mr-2"></i>
                    <span>Expira em {diasRestantes}</span>
                  </div>
                )}
                {missao.concluida && missao.dataConclusao && (
                  <div className="flex items-center text-sm text-green-600 font-medium">
                    <i className="ri-check-line mr-2"></i>
                    <span>Concluída em {new Date(missao.dataConclusao).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
              </div>

              {/* Status Badge */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  missao.concluida
                    ? 'bg-green-100 text-green-700'
                    : missao.progresso > 0
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {missao.concluida ? 'Concluída' : missao.progresso > 0 ? 'Em Andamento' : 'Aberta'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {missoesFiltradas.length === 0 && (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-flag-line text-4xl text-slate-400"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Nenhuma missão encontrada</h3>
          <p className="text-sm text-slate-600">Não há missões {filtro} nesta campanha no momento.</p>
        </div>
      )}
    </div>
  );
};

export default ParticipanteMissoes;
